import type { PropertyIn, PropertyPath } from '@/scopes/common/domain/types'
import type { CollectionLabelType, LabelState } from '@sunwebgroup/ui-components'

export interface Label {
  text: string
  type: LabelState
  placeholders?: Record<string, string>
}

export interface CollectionLabel {
  text: string
  type: CollectionLabelType
}

export interface Participant {
  id: string
  name: string
  status: Label
  room: string
  assigned: boolean
  amountDue: number
  email: string
  bookingNumber: number
  telephoneNumber: string
  mobileNumber: string
  skiPass: string
  hasSkiMaterial: boolean
  hasTotallySweater: boolean
  isBusAssignmentProcessing?: boolean
}

export interface Occupancy {
  occupied: number
  max: number
}

export interface Room {
  id: string
  name: string
  type: string
  occupancy: Occupancy
  occupancyText: string
  accommodationName: string
  participants: string[]
  participantsId: number[]
}

export interface Bus {
  id: string
  arrivalLocation: string
  departureLocation: string
  direction: 'Inbound' | 'Outbound' | undefined
  busNumber: string
  participantsId: number[]
}

export interface GroupBookingData {
  group: {
    name: string
    location: string
    period: string
    imgSrc: string
    startingDate: string
  }
  labels: Label[]
  participants: Record<string, Participant>
  rooms: Record<string, Room>
  buses: Record<string, Bus>
}

export type GroupResponsibleTabName = 'booking' | 'participants' | 'rooms' | 'bus'

interface GroupResponsibleStoreActions {
  initialize: (state: Partial<GroupResponsibleState>) => void
  setIn: (path: PropertyPath<GroupResponsibleState>, value: PropertyIn<GroupResponsibleState, typeof path>) => void
  changeTab: (tab: GroupResponsibleTabName) => void
  changeExtraInfoId: (id: string) => void
  toggleParticipantSelection: (participantId: string) => void
  selectAllParticipants: (participantIds: string[]) => void
  clearAllSelections: () => void
  toggleIsEditingParticipants: (isEditing: boolean) => void
}

export type GroupResponsibleStore = GroupResponsibleState & GroupResponsibleStoreActions

export interface GroupResponsibleState {
  bookingData: {
    group: {
      name: string
      imgSrc: string
      location: string
      period: string
      startingDate: string
    }
    labels: Label[] | undefined
  }
  activeTab: GroupResponsibleTabName
  participants: Record<string, Participant>
  rooms: Record<string, Room>
  buses: Record<string, Bus>
  activeExtraInfoId: string
  selectedParticipantIds: Set<string>
  isEditingParticipants: boolean
  retrieveBookingData: {
    loading: boolean
    error: boolean
  }
}

export const ParticipantFilterValues = {
  ALL: 'all',
  NOT_PAID: 'not_paid',
  PARTIAL_PAID: 'partial_paid',
  PAID: 'paid',
} as const

export type ParticipantFilterType = (typeof ParticipantFilterValues)[keyof typeof ParticipantFilterValues]

export const BusFilterValues = {
  ALL: 'all',
  NOT_BOOKED: 'not_booked',
  BOOKED: 'booked',
} as const

export type BusFilterType = (typeof BusFilterValues)[keyof typeof BusFilterValues]

export const RoomFilterValues = {
  ALL: 'all',
  OCCUPIED: 'occupied',
  NOT_OCCUPIED: 'not_occupied',
} as const

export type RoomFilterType = (typeof RoomFilterValues)[keyof typeof RoomFilterValues]
